/**
 * MemoryDashboardPage Component
 *
 * Surfaces the P3 memory backend: Hybrid Vector Memory Tiering,
 * Temporal Context Orchestration, and Passive Correction Harvesting.
 *
 * Copyright (c) 2026 Equilateral AI. All rights reserved.
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Table, Spinner, Select, Button, TextInput, Alert } from 'flowbite-react';
import { Tabs } from 'flowbite-react';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  searchMemory,
  getMemoryEntries,
  consolidateMemory,
  getMemoryStats,
  getTemporalPatterns,
  getTemporalPredictions,
  runCorrectionHarvest,
  getHarvestedCorrections,
  getCorrectionHarvestStats,
  type MemoryEntry,
  type MemoryStats,
  type TemporalPattern,
  type TemporalPrediction,
  type HarvestedCorrection,
} from '../api/memory';

// =============================================================================
// HELPERS
// =============================================================================

const TIER_COLORS: Record<string, string> = {
  hot: 'failure',
  warm: 'warning',
  cool: 'info',
  cold: 'gray',
};

const TIER_BG_COLORS: Record<string, string> = {
  hot: 'bg-red-50 border-red-200',
  warm: 'bg-orange-50 border-orange-200',
  cool: 'bg-blue-50 border-blue-200',
  cold: 'bg-gray-50 border-gray-200',
};

const TIER_TEXT_COLORS: Record<string, string> = {
  hot: 'text-red-700',
  warm: 'text-orange-700',
  cool: 'text-blue-700',
  cold: 'text-gray-700',
};

const TIER_COUNT_COLORS: Record<string, string> = {
  hot: 'text-red-900',
  warm: 'text-orange-900',
  cool: 'text-blue-900',
  cold: 'text-gray-900',
};

function truncate(text: string, maxLen: number): string {
  if (!text) return '';
  return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function signalStrengthColor(strength: string): string {
  switch (strength) {
    case 'strong': return 'success';
    case 'moderate': return 'warning';
    case 'weak': return 'failure';
    default: return 'gray';
  }
}

// =============================================================================
// TAB 1: MEMORY TIERS
// =============================================================================

const MemoryTiersTab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<MemoryStats | null>(null);
  const [entries, setEntries] = useState<MemoryEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isConsolidating, setIsConsolidating] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await getMemoryStats(clientId);
      if (response.success && response.data?.Records) {
        setStats(Array.isArray(response.data.Records) ? response.data.Records[0] : response.data.Records);
      }
    } catch (error) {
      console.error('Failed to fetch memory stats:', error);
    }
  }, [clientId]);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const opts: Record<string, string | number> = {};
      if (tierFilter) opts.Tier = tierFilter;
      if (typeFilter) opts.Memory_Type = typeFilter;

      let response;
      if (searchQuery.trim()) {
        setIsSearching(true);
        response = await searchMemory(clientId, searchQuery.trim(), opts);
        setIsSearching(false);
      } else {
        response = await getMemoryEntries(clientId, opts);
      }
      if (response.success && response.data?.Records) {
        setEntries(response.data.Records);
      }
    } catch (error) {
      console.error('Failed to fetch memory entries:', error);
      toast.error('Failed to load memory entries');
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  }, [clientId, searchQuery, tierFilter, typeFilter]);

  useEffect(() => {
    fetchStats();
    fetchEntries();
  }, [fetchStats, fetchEntries]);

  const handleConsolidate = async () => {
    setIsConsolidating(true);
    try {
      const response = await consolidateMemory(clientId);
      if (response.success) {
        toast.success('Memory consolidation complete');
        fetchStats();
        fetchEntries();
      } else {
        toast.error(response.message || 'Consolidation failed');
      }
    } catch (error) {
      console.error('Consolidation error:', error);
      toast.error('Failed to run consolidation');
    } finally {
      setIsConsolidating(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchEntries();
  };

  return (
    <div className="space-y-6">
      {/* Tier Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(['hot', 'warm', 'cool', 'cold'] as const).map((tier) => {
          const count = stats ? stats[`${tier}_count` as keyof MemoryStats] : 0;
          return (
            <Card key={tier} className={`${TIER_BG_COLORS[tier]} border`}>
              <div className="text-center">
                <p className={`text-xs font-semibold uppercase tracking-wider ${TIER_TEXT_COLORS[tier]}`}>
                  {tier}
                </p>
                <p className={`text-3xl font-bold mt-1 ${TIER_COUNT_COLORS[tier]}`}>
                  {typeof count === 'number' ? count.toLocaleString() : '0'}
                </p>
                <p className="text-xs text-gray-500 mt-1">entries</p>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
          <span>
            Total entries: <strong>
              {((stats.hot_count || 0) + (stats.warm_count || 0) + (stats.cool_count || 0) + (stats.cold_count || 0)).toLocaleString()}
            </strong>
          </span>
          <span>
            Avg access count: <strong>{(stats.avg_access_count || 0).toFixed(1)}</strong>
          </span>
          <span>
            Total consolidations: <strong>{(stats.total_consolidations || 0).toLocaleString()}</strong>
          </span>
        </div>
      )}

      {/* Search & Filters */}
      <Card>
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <TextInput
              icon={MagnifyingGlassIcon}
              placeholder="Search memory entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div>
            <Select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
              <option value="">All tiers</option>
              <option value="hot">Hot</option>
              <option value="warm">Warm</option>
              <option value="cool">Cool</option>
              <option value="cold">Cold</option>
            </Select>
          </div>
          <div>
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All types</option>
              <option value="episodic">Episodic</option>
              <option value="semantic">Semantic</option>
              <option value="procedural">Procedural</option>
            </Select>
          </div>
          <Button type="submit" color="light" disabled={isSearching}>
            {isSearching ? <Spinner size="sm" /> : 'Search'}
          </Button>
          <Button color="purple" onClick={handleConsolidate} disabled={isConsolidating}>
            {isConsolidating ? <Spinner size="sm" className="mr-2" /> : <ArrowPathIcon className="w-4 h-4 mr-2" />}
            Consolidate
          </Button>
        </form>
      </Card>

      {/* Results Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : entries.length === 0 ? (
        <Alert color="info">No memory entries found.</Alert>
      ) : (
        <div className="overflow-x-auto">
          <Table striped>
            <Table.Head>
              <Table.HeadCell>Content</Table.HeadCell>
              <Table.HeadCell>Agent</Table.HeadCell>
              <Table.HeadCell>Type</Table.HeadCell>
              <Table.HeadCell>Tier</Table.HeadCell>
              <Table.HeadCell>Access Count</Table.HeadCell>
              <Table.HeadCell>Last Accessed</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {entries.map((entry) => (
                <Table.Row key={entry.Entry_ID}>
                  <Table.Cell className="max-w-xs">
                    <span title={entry.Content}>{truncate(entry.Content, 80)}</span>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">{entry.Agent_Name}</Table.Cell>
                  <Table.Cell>
                    <Badge color="indigo">{entry.Memory_Type}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={TIER_COLORS[entry.Tier] || 'gray'}>{entry.Tier}</Badge>
                  </Table.Cell>
                  <Table.Cell>{entry.Access_Count}</Table.Cell>
                  <Table.Cell className="whitespace-nowrap">{formatDate(entry.Last_Accessed_At)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// TAB 2: TEMPORAL PATTERNS
// =============================================================================

const TemporalPatternsTab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [patterns, setPatterns] = useState<TemporalPattern[]>([]);
  const [predictions, setPredictions] = useState<TemporalPrediction[]>([]);
  const [agentFilter, setAgentFilter] = useState('');
  const [patternTypeFilter, setPatternTypeFilter] = useState('');
  const [minConfidence, setMinConfidence] = useState('');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const patternOpts: Record<string, string | number> = {};
      if (agentFilter) patternOpts.Agent_Name = agentFilter;
      if (patternTypeFilter) patternOpts.Pattern_Type = patternTypeFilter;
      if (minConfidence) patternOpts.min_confidence = parseFloat(minConfidence);

      const predOpts: Record<string, string> = {};
      if (agentFilter) predOpts.Agent_Name = agentFilter;

      const [patternsRes, predictionsRes] = await Promise.all([
        getTemporalPatterns(clientId, patternOpts),
        getTemporalPredictions(clientId, predOpts),
      ]);

      if (patternsRes.success && patternsRes.data?.Records) {
        setPatterns(patternsRes.data.Records);
      }
      if (predictionsRes.success && predictionsRes.data?.Records) {
        setPredictions(predictionsRes.data.Records);
      }
    } catch (error) {
      console.error('Failed to fetch temporal data:', error);
      toast.error('Failed to load temporal patterns');
    } finally {
      setIsLoading(false);
    }
  }, [clientId, agentFilter, patternTypeFilter, minConfidence]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Agent</label>
            <TextInput
              placeholder="Filter by agent..."
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              sizing="sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Pattern Type</label>
            <Select value={patternTypeFilter} onChange={(e) => setPatternTypeFilter(e.target.value)} sizing="sm">
              <option value="">All types</option>
              <option value="sequential">Sequential</option>
              <option value="periodic">Periodic</option>
              <option value="conditional">Conditional</option>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Min Confidence</label>
            <TextInput
              type="number"
              placeholder="0.0 - 1.0"
              value={minConfidence}
              onChange={(e) => setMinConfidence(e.target.value)}
              sizing="sm"
              min="0"
              max="1"
              step="0.1"
            />
          </div>
        </div>
      </Card>

      {/* Patterns Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Patterns</h3>
        {patterns.length === 0 ? (
          <Alert color="info">No temporal patterns found.</Alert>
        ) : (
          <div className="overflow-x-auto">
            <Table striped>
              <Table.Head>
                <Table.HeadCell>Agent</Table.HeadCell>
                <Table.HeadCell>Pattern Type</Table.HeadCell>
                <Table.HeadCell>Trigger Conditions</Table.HeadCell>
                <Table.HeadCell>Confidence</Table.HeadCell>
                <Table.HeadCell>Activations</Table.HeadCell>
                <Table.HeadCell>Success Rate</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {patterns.map((pattern) => {
                  const successRate = pattern.Activation_Count > 0
                    ? ((pattern.Success_Count / pattern.Activation_Count) * 100).toFixed(0)
                    : '0';
                  return (
                    <Table.Row key={pattern.Pattern_ID}>
                      <Table.Cell className="whitespace-nowrap">{pattern.Agent_Name}</Table.Cell>
                      <Table.Cell>
                        <Badge color="purple">{pattern.Pattern_Type}</Badge>
                      </Table.Cell>
                      <Table.Cell className="max-w-xs">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded break-all">
                          {truncate(JSON.stringify(pattern.Trigger_Conditions), 60)}
                        </code>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-indigo-500 h-2 rounded-full"
                              style={{ width: `${(pattern.Confidence * 100).toFixed(0)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{(pattern.Confidence * 100).toFixed(0)}%</span>
                        </div>
                      </Table.Cell>
                      <Table.Cell>{pattern.Activation_Count}</Table.Cell>
                      <Table.Cell>{successRate}%</Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      {/* Predictions Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Predictions</h3>
        {predictions.length === 0 ? (
          <Alert color="info">No temporal predictions found.</Alert>
        ) : (
          <div className="overflow-x-auto">
            <Table striped>
              <Table.Head>
                <Table.HeadCell>Agent</Table.HeadCell>
                <Table.HeadCell>Predicted Action</Table.HeadCell>
                <Table.HeadCell>Confidence</Table.HeadCell>
                <Table.HeadCell>Was Correct</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {predictions.map((pred) => (
                  <Table.Row key={pred.Prediction_ID}>
                    <Table.Cell className="whitespace-nowrap">{pred.Agent_Name}</Table.Cell>
                    <Table.Cell>{truncate(pred.Predicted_Action, 80)}</Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-indigo-500 h-2 rounded-full"
                            style={{ width: `${(pred.Confidence * 100).toFixed(0)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">{(pred.Confidence * 100).toFixed(0)}%</span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {pred.Was_Correct === true && <CheckIcon className="w-5 h-5 text-green-600" />}
                      {pred.Was_Correct === false && <XMarkIcon className="w-5 h-5 text-red-500" />}
                      {pred.Was_Correct === null && <span className="text-gray-400 text-xs">pending</span>}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// TAB 3: CORRECTION HARVESTING
// =============================================================================

const CorrectionHarvestingTab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [corrections, setCorrections] = useState<HarvestedCorrection[]>([]);
  const [harvestStats, setHarvestStats] = useState<Record<string, unknown> | null>(null);
  const [isHarvesting, setIsHarvesting] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [correctionsRes, statsRes] = await Promise.all([
        getHarvestedCorrections(clientId),
        getCorrectionHarvestStats(clientId),
      ]);

      if (correctionsRes.success && correctionsRes.data?.Records) {
        setCorrections(correctionsRes.data.Records);
      }
      if (statsRes.success && statsRes.data?.Records) {
        const statsData = Array.isArray(statsRes.data.Records) ? statsRes.data.Records[0] : statsRes.data.Records;
        setHarvestStats(statsData);
      }
    } catch (error) {
      console.error('Failed to fetch correction data:', error);
      toast.error('Failed to load correction data');
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleHarvest = async () => {
    setIsHarvesting(true);
    try {
      const response = await runCorrectionHarvest(clientId);
      if (response.success) {
        toast.success('Correction harvest complete');
        fetchData();
      } else {
        toast.error(response.message || 'Harvest failed');
      }
    } catch (error) {
      console.error('Harvest error:', error);
      toast.error('Failed to run correction harvest');
    } finally {
      setIsHarvesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  // Compute source type counts for badge display
  const sourceTypeCounts: Record<string, number> = {};
  corrections.forEach((c) => {
    sourceTypeCounts[c.Source_Type] = (sourceTypeCounts[c.Source_Type] || 0) + 1;
  });

  const totalCorrections = harvestStats?.total_corrections ?? corrections.length;
  const promotedCount = harvestStats?.promoted_count ?? 0;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Corrections</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{Number(totalCorrections).toLocaleString()}</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Promoted</p>
            <p className="text-3xl font-bold text-green-700 mt-1">{Number(promotedCount).toLocaleString()}</p>
          </div>
        </Card>
        <Card>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2 text-center">By Source Type</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {Object.entries(sourceTypeCounts).map(([type, count]) => (
                <Badge key={type} color="indigo">
                  {type}: {count}
                </Badge>
              ))}
              {Object.keys(sourceTypeCounts).length === 0 && (
                <span className="text-xs text-gray-400">No data</span>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Run Harvest Button */}
      <div className="flex justify-end">
        <Button color="purple" onClick={handleHarvest} disabled={isHarvesting}>
          {isHarvesting ? <Spinner size="sm" className="mr-2" /> : <ArrowPathIcon className="w-4 h-4 mr-2" />}
          Run Harvest
        </Button>
      </div>

      {/* Corrections Table */}
      {corrections.length === 0 ? (
        <Alert color="info">No harvested corrections found.</Alert>
      ) : (
        <div className="overflow-x-auto">
          <Table striped>
            <Table.Head>
              <Table.HeadCell>Agent</Table.HeadCell>
              <Table.HeadCell>Source Type</Table.HeadCell>
              <Table.HeadCell>Extracted Rule</Table.HeadCell>
              <Table.HeadCell>Domain</Table.HeadCell>
              <Table.HeadCell>Confidence</Table.HeadCell>
              <Table.HeadCell>Signal Strength</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {corrections.map((correction) => (
                <Table.Row key={correction.Harvest_ID}>
                  <Table.Cell className="whitespace-nowrap">{correction.Agent_Name}</Table.Cell>
                  <Table.Cell>
                    <Badge color="purple">{correction.Source_Type}</Badge>
                  </Table.Cell>
                  <Table.Cell className="max-w-sm">
                    <span title={correction.Extracted_Rule}>{truncate(correction.Extracted_Rule, 80)}</span>
                  </Table.Cell>
                  <Table.Cell>{correction.Domain}</Table.Cell>
                  <Table.Cell>{(correction.Confidence * 100).toFixed(0)}%</Table.Cell>
                  <Table.Cell>
                    <Badge color={signalStrengthColor(correction.Signal_Strength)}>
                      {correction.Signal_Strength}
                    </Badge>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN PAGE
// =============================================================================

export const MemoryDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const clientId = user?.client_id;

  if (!clientId) {
    return (
      <Alert color="warning">
        No client context available. Please ensure you are logged in.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Memory Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">
          Hybrid Vector Memory, Temporal Context, and Correction Harvesting
        </p>
      </div>

      <Tabs aria-label="Memory dashboard tabs" variant="underline">
        <Tabs.Item active title="Memory Tiers">
          <MemoryTiersTab clientId={clientId} />
        </Tabs.Item>
        <Tabs.Item title="Temporal Patterns">
          <TemporalPatternsTab clientId={clientId} />
        </Tabs.Item>
        <Tabs.Item title="Correction Harvesting">
          <CorrectionHarvestingTab clientId={clientId} />
        </Tabs.Item>
      </Tabs>
    </div>
  );
};

export default MemoryDashboardPage;
